import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Chargement, Erreur, Modale } from './communs.tsx';

/**
 * Aperçu avant impression — TOUS les PDF de l'admin passent par ici.
 *
 * Avant, chaque bouton « Imprimer » déclenchait un téléchargement direct
 * (`doc.save`) : impossible de vérifier le contenu avant de le donner au
 * client, ou de simplement l'imprimer sans d'abord l'ouvrir depuis les
 * téléchargements. `ouvrir()` affiche le PDF dans une fenêtre, avec un
 * bouton Imprimer ET un bouton Télécharger — le choix reste à qui regarde.
 *
 * Le bouton Imprimer OUVRE le PDF dans un nouvel onglet plutôt que
 * d'appeler `iframe.contentWindow.print()` : ce dernier imprime le cadre
 * englobant selon les navigateurs, pas fiablement le PDF lui-même — un
 * gérant se retrouvait parfois à imprimer l'écran plutôt que le document.
 * Le nouvel onglet ouvre la visionneuse PDF NATIVE du navigateur, avec son
 * propre bouton Imprimer — c'est elle qui imprime le vrai PDF, pas nous.
 */

type EtatApercu =
  | { statut: 'chargement' }
  | { statut: 'pret'; url: string; nomFichier: string }
  | { statut: 'erreur'; erreur: unknown };

interface ContexteApercu {
  /**
   * `construire` ne s'exécute qu'ICI, pas avant : générer un PDF a un coût
   * (chargement de `jspdf`, parfois un aller-retour réseau pour le détail
   * d'une vente) qu'il ne faut payer qu'au clic, jamais par avance.
   */
  ouvrir: (construire: () => Promise<{ blob: Blob; nomFichier: string }>) => void;
}

const Contexte = createContext<ContexteApercu | null>(null);

export function useApercuPdf(): ContexteApercu {
  const valeur = useContext(Contexte);
  if (valeur === null) {
    throw new Error('useApercuPdf doit être appelé sous <FournisseurApercuPdf>.');
  }
  return valeur;
}

export function FournisseurApercuPdf({ children }: { children: ReactNode }): ReactNode {
  const [etat, setEtat] = useState<EtatApercu | null>(null);
  const urlActuelle = useRef<string | null>(null);

  // L'URL d'objet vit tant que la fenêtre est ouverte ; au-delà, elle
  // fuirait de la mémoire — un gérant qui imprime vingt factures d'affilée
  // ne doit pas accumuler vingt PDF fantômes dans l'onglet.
  useEffect(
    () => () => {
      if (urlActuelle.current !== null) {
        URL.revokeObjectURL(urlActuelle.current);
      }
    },
    [],
  );

  function fermer(): void {
    if (urlActuelle.current !== null) {
      URL.revokeObjectURL(urlActuelle.current);
      urlActuelle.current = null;
    }
    setEtat(null);
  }

  function ouvrir(construire: () => Promise<{ blob: Blob; nomFichier: string }>): void {
    setEtat({ statut: 'chargement' });
    construire()
      .then(({ blob, nomFichier }) => {
        const url = URL.createObjectURL(blob);
        urlActuelle.current = url;
        setEtat({ statut: 'pret', url, nomFichier });
      })
      .catch((erreur: unknown) => {
        setEtat({ statut: 'erreur', erreur });
      });
  }

  return (
    <Contexte.Provider value={{ ouvrir }}>
      {children}

      {etat !== null && (
        <Modale onFermer={fermer} taille="large">
          <div className="apercu-pdf">
            <div className="apercu-pdf__barre">
              <strong>{etat.statut === 'pret' ? `${etat.nomFichier}.pdf` : 'Aperçu'}</strong>
              <div className="ligne">
                {etat.statut === 'pret' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        window.open(etat.url, '_blank');
                      }}
                    >
                      Imprimer
                    </button>
                    <a
                      className="bouton--discret"
                      href={etat.url}
                      download={`${etat.nomFichier}.pdf`}
                    >
                      Télécharger
                    </a>
                  </>
                )}
                <button type="button" className="bouton--discret" onClick={fermer}>
                  Fermer
                </button>
              </div>
            </div>

            {etat.statut === 'chargement' && (
              <div className="apercu-pdf__etat">
                <Chargement />
              </div>
            )}
            {etat.statut === 'erreur' && (
              <div className="apercu-pdf__etat">
                <Erreur erreur={etat.erreur} />
              </div>
            )}
            {etat.statut === 'pret' && (
              <iframe src={etat.url} title="Aperçu avant impression" className="apercu-pdf__cadre" />
            )}
          </div>
        </Modale>
      )}
    </Contexte.Provider>
  );
}
